import {Coordinate} from '~redux/types/images';
import {AnnotationItem} from '~redux/reducers/annotatorReducer';

import {
  chipSourceFromLabelSource,
  PredictionChipData,
  ThumbnailChipSource,
} from '~components/common/images/PredictionChip';

export function getLabelChipPosition(coordinates: Coordinate[]): Coordinate {
  const y = Math.max(...coordinates.map((point) => point.y));
  const x = [...coordinates]
    .sort((point) => point.x)
    .reverse()
    .find((point) => point.y === y)!.x;

  return {x, y};
}

interface RoiImageScope {
  perspective?: number | null;
  productType?: string | null;
}

export function toRoiScope(entry: ModelRoisSchema): RoiScope {
  return {perspective: entry.perspective ?? null, productType: entry.productType ?? null};
}

export function isSpecificRoiScope(scope: RoiScope): boolean {
  return scope.perspective !== null || scope.productType !== null;
}

export function roiScopeMatchesImage(scope: RoiScope, image: RoiImageScope): boolean {
  const perspectiveMatches = scope.perspective === null || scope.perspective === image.perspective;
  const productTypeMatches = scope.productType === null || scope.productType === image.productType;
  return perspectiveMatches && productTypeMatches;
}

export function roiAppliesToImage(roi: RoiWithMetadata, image: RoiImageScope): boolean {
  return roi.scopes.some((scope) => roiScopeMatchesImage(scope, image));
}

export function selectRoiScopesForImage<T>(items: T[], getScope: (item: T) => RoiScope, image: RoiImageScope): T[] {
  const matchingItems = items.filter((item) => roiScopeMatchesImage(getScope(item), image));
  const hasSpecificScope = matchingItems.some((item) => isSpecificRoiScope(getScope(item)));
  return hasSpecificScope ? matchingItems.filter((item) => isSpecificRoiScope(getScope(item))) : matchingItems;
}

export function getRoiLabelChipPosition(coordinates: Coordinate[]): Coordinate {
  const x = Math.min(...coordinates.map((point) => point.x));
  const y = [...coordinates].sort((a, b) => a.y - b.y).find((point) => point.x === x)!.y;

  return {x, y};
}

export function annotationItemToPredictionChipData(
  annotationItem: AnnotationItem,
  options?: {modelName?: string},
): PredictionChipData {
  // When `source` is set (e.g. server-loaded items via createAnnotationItem) derive the chip
  // source from it; otherwise fall back to the machine/curation flags so items constructed
  // outside createAnnotationItem don't silently render as prediction chips.
  const chipSource: ThumbnailChipSource = annotationItem.source
    ? chipSourceFromLabelSource(annotationItem.source)
    : annotationItem.isCurationMachinePrediction
      ? 'synthetic'
      : annotationItem.isMachinePrediction
        ? 'prediction'
        : 'human';

  return {
    labelId: annotationItem.labelId,
    alias: annotationItem.alias ?? annotationItem.labelId,
    color: annotationItem.color,
    condition: annotationItem.condition as PredictionChipData['condition'],
    probability: annotationItem.probability,
    isMachinePrediction: annotationItem.isMachinePrediction,
    isDefaultPrediction: annotationItem.isDefaultPrediction,
    source: annotationItem.source,
    unstable: annotationItem.unstable,
    userEmail: chipSource === 'human' ? annotationItem.userEmail : undefined,
    modelName: options?.modelName,
  };
}
